/** biome-ignore-all lint/nursery/useExplicitType: Zod schema inference */
import { z } from 'zod';
import type { IAPI } from '../../apiResponse';
import { createApiResponseSchema } from '../../apiResponse';

const billingSubscriptionRowSchema = z.object({
  id: z.string(),
  userId: z.string(),
  planKey: z.string(),
  status: z.string(),
  platform: z.string(),
  originalTransactionId: z.string(),
  latestTransactionId: z.string().nullable(),
  purchaseDate: z.number(),
  expiresDate: z.number().nullable(),
  cancelledDate: z.number().nullable(),
  autoRenewing: z.number(),
  gracePeriodExpiresDate: z.number().nullable(),
  environment: z.string(),
  lastReconciledAt: z.number().nullable(),
  createdAt: z.number(),
  updatedAt: z.number(),
});

const billingTransactionRowSchema = z.object({
  id: z.string(),
  userId: z.string(),
  planKey: z.string(),
  platform: z.string(),
  transactionId: z.string(),
  originalTransactionId: z.string(),
  receiptData: z.string(),
  verificationStatus: z.string(),
  verificationResponse: z.string().nullable(),
  priceUsd: z.number().nullable(),
  currency: z.string().nullable(),
  purchaseDate: z.number(),
  expiresDate: z.number().nullable(),
  isTrial: z.number(),
  isIntroOffer: z.number(),
  createdAt: z.number(),
});

const billingAuditLogRowSchema = z.object({
  id: z.string(),
  subscriptionId: z.string(),
  userId: z.string(),
  action: z.string(),
  previousStatus: z.string().nullable(),
  newStatus: z.string().nullable(),
  previousExpiresDate: z.number().nullable(),
  newExpiresDate: z.number().nullable(),
  source: z.string(),
  metadata: z.string().nullable(),
  createdAt: z.number(),
});

const billingWebhookEventRowSchema = z.object({
  id: z.string(),
  userId: z.string().nullable(),
  platform: z.string(),
  eventType: z.string(),
  rawPayload: z.string(),
  transactionId: z.string().nullable(),
  messageId: z.string().nullable(),
  payloadHash: z.string().nullable(),
  processedAt: z.number().nullable(),
  error: z.string().nullable(),
  createdAt: z.number(),
});

export const userBillingHistoryV1DataSchema = z.object({
  userId: z.string(),
  subscriptions: z.array(billingSubscriptionRowSchema),
  transactions: z.array(billingTransactionRowSchema),
  auditLog: z.array(billingAuditLogRowSchema),
  webhookEvents: z.array(billingWebhookEventRowSchema),
});

export type IUserBillingHistoryV1Data = z.infer<typeof userBillingHistoryV1DataSchema>;
export type IUserBillingSubscriptionRow = z.infer<typeof billingSubscriptionRowSchema>;
export type IUserBillingTransactionRow = z.infer<typeof billingTransactionRowSchema>;
export type IUserBillingAuditLogRow = z.infer<typeof billingAuditLogRowSchema>;
export type IUserBillingWebhookEventRow = z.infer<typeof billingWebhookEventRowSchema>;

export const getUserBillingHistoryV1ResponseSchema = createApiResponseSchema(
  userBillingHistoryV1DataSchema,
) as z.ZodSchema<IAPI<IUserBillingHistoryV1Data>>;

export type IGetUserBillingHistoryV1Response = IAPI<IUserBillingHistoryV1Data>;
